import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { getSensorReadings, getLatestSensorReadings, SensorReading } from '../api/client';

// ------------------------------------------------------------------
// Constants
// ------------------------------------------------------------------

const HOUR_OPTIONS = [
  { label: 'שעה', value: 1 },
  { label: '6 שעות', value: 6 },
  { label: '24 שעות', value: 24 },
  { label: '48 שעות', value: 48 },
];

const LINE_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#06b6d4', '#6366f1', '#ec4899', '#a855f7',
  '#14b8a6', '#f43f5e',
];

const AUTO_REFRESH_MS = 30_000;

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function formatTime(iso: string, hours: number): string {
  try {
    const d = new Date(iso);
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    const ss = d.getSeconds().toString().padStart(2, '0');
    if (hours > 24) {
      const dd = d.getDate().toString().padStart(2, '0');
      const mo = (d.getMonth() + 1).toString().padStart(2, '0');
      return `${dd}/${mo} ${hh}:${mm}`;
    }
    return `${hh}:${mm}:${ss}`;
  } catch {
    return iso;
  }
}

function formatValue(raw: string): string {
  const n = parseFloat(raw);
  return isNaN(n) ? raw : n.toFixed(2);
}

function labelFor(r: SensorReading): string {
  return r.register_name && r.register_name !== r.register_address
    ? r.register_name
    : `Reg ${r.register_address}`;
}

interface ChartPoint {
  time: string;
  [addr: string]: string | number;
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

export default function Sensors() {
  const [hours, setHours] = useState(1);
  const [device, setDevice] = useState('');
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [latest, setLatest] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [hist, lat] = await Promise.all([
        getSensorReadings(hours, device || undefined),
        getLatestSensorReadings(device || undefined),
      ]);
      setReadings(hist);
      setLatest(lat);
      setLastUpdated(new Date());
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'שגיאה בטעינת נתונים';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [hours, device]);

  // Fetch on mount and whenever hours / device changes
  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, AUTO_REFRESH_MS);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchData]);

  // Derive device list from all readings
  const devices = useMemo(() => {
    const ids = new Set([...readings, ...latest].map(r => r.device_id).filter(Boolean));
    return Array.from(ids).sort();
  }, [readings, latest]);

  // Register metadata: address → display name
  const registers = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of readings) {
      if (!map.has(r.register_address)) map.set(r.register_address, labelFor(r));
    }
    return Array.from(map.entries())
      .map(([addr, name]) => ({ addr, name }))
      .sort((a, b) => a.addr.localeCompare(b.addr, undefined, { numeric: true }));
  }, [readings]);

  // Build chart points: bucket by ISO timestamp, each register as a numeric field
  const chartData = useMemo<ChartPoint[]>(() => {
    if (readings.length === 0) return [];
    const byTime = new Map<string, ChartPoint>();
    for (const r of readings) {
      const val = parseFloat(r.value);
      if (isNaN(val)) continue;
      if (!byTime.has(r.time)) byTime.set(r.time, { time: formatTime(r.time, hours) });
      byTime.get(r.time)![r.register_address] = Math.round(val * 1000) / 1000;
    }
    return Array.from(byTime.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, point]) => point);
  }, [readings, hours]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div className="max-w-7xl mx-auto space-y-5" dir="rtl">

      {/* ── Page header ── */}
      <div className="flex items-center justify-between">
        <h1
          className="text-2xl font-semibold text-espresso-900"
          style={{ fontFamily: "'Frank Ruhl Libre', serif" }}
        >
          חיישנים
        </h1>
        {lastUpdated && (
          <span className="text-xs text-espresso-400">
            עודכן: {lastUpdated.toLocaleTimeString('he-IL')}
            {loading && <span className="mr-2 text-gold-500">טוען…</span>}
          </span>
        )}
      </div>

      {/* ── Controls bar ── */}
      <div className="bg-white/70 backdrop-blur rounded-2xl border border-espresso-200/60 shadow-soft p-4 flex flex-wrap gap-3 items-center">

        {/* Time range buttons */}
        <div className="flex gap-1">
          {HOUR_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setHours(opt.value)}
              disabled={loading}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50 ${
                hours === opt.value
                  ? 'bg-espresso-800 text-cream-100'
                  : 'bg-espresso-100 text-espresso-700 hover:bg-espresso-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-espresso-200" />

        {/* Device selector */}
        <select
          value={device}
          onChange={e => setDevice(e.target.value)}
          className="px-3 py-1.5 rounded-xl text-sm border border-espresso-200 bg-cream-50 text-espresso-800 focus:outline-none focus:ring-2 focus:ring-gold-400"
        >
          <option value="">כל המכשירים</option>
          {devices.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Auto-refresh toggle */}
        <label className="flex items-center gap-2 text-sm text-espresso-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={e => setAutoRefresh(e.target.checked)}
            className="rounded accent-gold-500"
          />
          רענון (30s)
        </label>

        {/* Manual refresh */}
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-1.5 rounded-xl text-sm font-medium bg-espresso-800 text-cream-100 hover:bg-espresso-700 disabled:opacity-50 transition-all active:scale-95"
        >
          {loading ? '...' : '↻ רענן'}
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700 text-sm">
          ⚠ {error}
        </div>
      )}

      {/* ── Time-series chart ── */}
      <div className="bg-white/70 backdrop-blur rounded-2xl border border-espresso-200/60 shadow-soft p-6">
        <h2 className="text-sm font-semibold text-espresso-700 mb-4 uppercase tracking-wide">
          גרף לאורך זמן
        </h2>

        {loading && readings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3 text-espresso-400">
            <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">שולח שאילתה ל-Athena…</span>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-72 text-espresso-400 text-sm">
            אין נתונים לתקופה הנבחרת
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={chartData} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: '#6b5f52' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#6b5f52' }}
                width={52}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: '0.75rem', border: '1px solid #e2d0b3' }}
                formatter={(value: number | string, addr: string) => {
                  const reg = registers.find(r => r.addr === addr);
                  return [value, reg?.name ?? addr];
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(addr: string) => {
                  const reg = registers.find(r => r.addr === addr);
                  return reg?.name ?? addr;
                }}
              />
              {registers.map((reg, i) => (
                <Line
                  key={reg.addr}
                  type="monotone"
                  dataKey={reg.addr}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                  name={reg.addr}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Latest values grid ── */}
      {latest.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-espresso-700 mb-3 uppercase tracking-wide">
            ערכים אחרונים
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {latest.map((r, i) => {
              const colorIdx = registers.findIndex(reg => reg.addr === r.register_address);
              const color = LINE_COLORS[(colorIdx >= 0 ? colorIdx : i) % LINE_COLORS.length];
              return (
                <div
                  key={`${r.device_id}-${r.register_address}`}
                  className="bg-white/80 backdrop-blur rounded-2xl border border-espresso-200/60 shadow-soft p-4 text-center hover:shadow-medium transition-shadow"
                >
                  <div
                    className="w-2 h-2 rounded-full mx-auto mb-2"
                    style={{ backgroundColor: color }}
                  />
                  <div
                    className="text-xs text-espresso-500 mb-1 truncate leading-tight"
                    title={labelFor(r)}
                  >
                    {labelFor(r)}
                  </div>
                  <div
                    className="text-2xl font-bold text-espresso-900"
                    style={{ fontFamily: "'Frank Ruhl Libre', serif" }}
                  >
                    {formatValue(r.value)}
                  </div>
                  <div className="text-xs text-espresso-300 mt-1 truncate" title={r.device_id}>
                    {r.device_id}
                  </div>
                  <div className="text-[10px] text-espresso-300 mt-0.5">
                    {formatTime(r.time, 1)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
